import React from 'react';
import { Route } from 'react-router-dom';
import './App.css';
import { Admin, CustomRoutes, Resource } from 'react-admin';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { QueryClientProvider, QueryClient } from 'react-query';

import { ParseAuth, ParseClient } from './ra-data-parse';
import { dataProviderExtension } from './DataProviderExtension';
import {
	DOWNLOADABLE, FEEDBACK, TRADEUNION_NEWS, USER_PROFILE,
} from './constants/resources';
import {
	authExtension, withAnyRolesPermissions,
	withMasterPermissions,
} from './DataProviderExtension/authExtension';
import { createCategory, editCategory, listCategory } from './resources/Category';
import { createBusiness, EditBusiness, ListBusiness } from './resources/Business';
import { createTradeUnion, EditTradeUnion, listTradeUnion } from './resources/TradeUnion';
import { createTradeUnionAdmin, editTradeUnionAdmin, listTradeUnionAdmin } from './resources/TradeUnionAdmin';
import { ListProfile, editProfile, CreateProfile } from './resources/UserProfile';
import {
	createPromotion,
	EditPromotion,
	ListPromotions,
} from './resources/Promotion';
import { createPushMessage, editPushMessage, listPushMessage } from './resources/Push';
import { listReview, ShowReview, editReview } from './resources/Review';
import * as Feedback from './resources/Feedback';
import { ListLocation,	CreateLocation,	EditLocation } from './resources/Location';
import VerificationCode from './components/VerificationCode';
import Layout from './components/Layout';
import * as Downloadable from './resources/Downloadable';
import TradeUnionNewsEdit from './resources/TradeUnionNews';
import Dashboard from './components/Dashboard';
import PrintableBusiness from './resources/Business/PrintableBusiness';
import LoginPage from './components/LoginPage';
import { i18nProvider } from './i18n/provider';


dayjs.locale('ru');

const parseConfig = {
	URL: process.env.REACT_APP_PARSE_URL,
	JAVASCRIPT_KEY: process.env.REACT_APP_PARSE_JS_KEY,
	APP_ID: process.env.REACT_APP_PARSE_APP_ID,
};

const parseDataProvider = dataProviderExtension(ParseClient(parseConfig));
const parseAuthProvider = authExtension(ParseAuth(parseConfig));
const queryClient = new QueryClient();

const businessAdminRoles = ['master', 'businessAdmin'];
const tradeUnionAdminRoles = ['master', 'tradeUnionAdmin'];

const App = () => (
	<Admin
		dataProvider={parseDataProvider}
		authProvider={parseAuthProvider}
		i18nProvider={i18nProvider}
		layout={Layout}
		dashboard={Dashboard}
		loginPage={LoginPage}
	>
		{(permissions) => [
			<Resource
				options={{
					label: 'Партнеры',
					show: withAnyRolesPermissions(permissions, businessAdminRoles, true, false),
				}}
				name="Business"
				list={ListBusiness}
				edit={withAnyRolesPermissions(permissions, businessAdminRoles, EditBusiness)}
				create={withMasterPermissions(permissions, createBusiness)}
			/>,
			<Resource
				options={{
					label: 'Администратор партнера',
					show: withMasterPermissions(permissions, true, false),
				}}
				name="BusinessAdmin"
			/>,
			<Resource
				options={{
					label: 'Акции',
					show: withMasterPermissions(permissions, true, false),
				}}
				name="Promotion"
				list={ListPromotions}
				edit={withAnyRolesPermissions(permissions, businessAdminRoles, EditPromotion)}
				create={withAnyRolesPermissions(permissions, businessAdminRoles, createPromotion)}
			/>,
			<Resource
				options={{
					label: 'Категории',
					show: withMasterPermissions(permissions, true, false),
				}}
				name="Category"
				list={listCategory}
				edit={withMasterPermissions(permissions, editCategory)}
				create={withMasterPermissions(permissions, createCategory)}
			/>,
			<Resource
				options={{
					label: 'Локации',
					show: withMasterPermissions(permissions, true, false),
				}}
				name="Location"
				list={withMasterPermissions(permissions, ListLocation)}
				create={withMasterPermissions(permissions, CreateLocation)}
				edit={withMasterPermissions(permissions, EditLocation)}
			/>,
			<Resource
				options={{
					label: 'ППО',
					show: withMasterPermissions(permissions, true, false),
				}}
				name="TradeUnion"
				list={listTradeUnion}
				edit={withMasterPermissions(permissions, EditTradeUnion)}
				create={withMasterPermissions(permissions, createTradeUnion)}
			/>,
			<Resource
				options={{
					label: 'Администратор ППО',
					show: withMasterPermissions(permissions, true, false),
				}}
				name="TradeUnionAdmin"
				list={withMasterPermissions(permissions, listTradeUnionAdmin)}
				edit={withMasterPermissions(permissions, editTradeUnionAdmin)}
				create={withMasterPermissions(permissions, createTradeUnionAdmin)}
			/>,
			<Resource
				options={{
					label: 'Члены ППО',
					show: withAnyRolesPermissions(permissions, tradeUnionAdminRoles, true, false),
				}}
				name={USER_PROFILE}
				list={withAnyRolesPermissions(permissions, tradeUnionAdminRoles, ListProfile)}
				edit={withAnyRolesPermissions(permissions, tradeUnionAdminRoles, editProfile)}
				create={withAnyRolesPermissions(permissions, tradeUnionAdminRoles, CreateProfile)}
			/>,
			<Resource
				options={{
					label: 'Отзывы',
					show: withMasterPermissions(permissions, true, false),
				}}
				name="Review"
				list={listReview}
				show={ShowReview}
				edit={withMasterPermissions(permissions, editReview)}
			/>,
			<Resource
				options={{
					label: 'Push',
					show: withMasterPermissions(permissions, true, false),
				}}
				name="PushMessage"
				list={withMasterPermissions(permissions, listPushMessage)}
				edit={withMasterPermissions(permissions, editPushMessage)}
				create={withMasterPermissions(permissions, createPushMessage)}
			/>,
			<Resource
				options={{
					label: 'Feedback',
					show: withMasterPermissions(permissions, true, false),
				}}
				name={FEEDBACK}
				list={withMasterPermissions(permissions, Feedback.List)}
				create={withMasterPermissions(permissions, Feedback.Create)}
				show={withMasterPermissions(permissions, Feedback.Show)}
				edit={withMasterPermissions(permissions, Feedback.Edit)}
			/>,
			<Resource
				options={{
					label: 'Новости',
					show: withMasterPermissions(permissions, true, false),
				}}
				name={TRADEUNION_NEWS}
				edit={withMasterPermissions(permissions, TradeUnionNewsEdit)}
			/>,
			<Resource
				options={{
					label: 'Файлы',
					show: withMasterPermissions(permissions, true, false),
				}}
				name={DOWNLOADABLE}
				list={Downloadable.List}
				edit={Downloadable.Edit}
			/>,
			<Resource
				name="AppEvent"
				options={{
					label: 'Журнал событий',
					show: withMasterPermissions(permissions, true, false),
				}}
			/>,
			<CustomRoutes>
				{withAnyRolesPermissions(
					permissions,
					tradeUnionAdminRoles, (
						<Route
							path="/verificationCode"
							element={(
								<QueryClientProvider client={queryClient}>
									<VerificationCode />
								</QueryClientProvider>
							)}
						/>
					),
				)}
				{withAnyRolesPermissions(
					permissions,
					tradeUnionAdminRoles, (
						<Route
							path="/printableBusiness"
							element={(
								<PrintableBusiness />
							)}
						/>
					),
				)}
			</CustomRoutes>,
		]}
	</Admin>
);


export default App;
